<template>
  <div>
    <ca-heading text="Cadastro de Autônomos" />

    <ca-page>
      <ca-loader v-if="showLoading"></ca-loader>
      <ca-form
        v-else
        :key="formKey"
        :on-submit="onSubmit"
        :on-submit-success="onSubmitSuccess"
        :on-submit-error="onSubmitError"
        :error-message="errorMessage">
        <ca-row>
          <ca-col size="6">
            <ca-field label="Nome">
              <ca-input
                v-model="selfEmployed.selfEmployedName"
                required
                maxlength="60">
              </ca-input>
            </ca-field>
          </ca-col>
          <ca-col size="6">
            <job-description-select
              v-model="selfEmployed.jobDescription.id"
              required>
            </job-description-select>
          </ca-col>
          <ca-col size="6">
            <ca-field label="CPF">
              <ca-cpf-input
                v-model="selfEmployed.cpf"
                v-mask="'###.###.###-##'"
                required>
              </ca-cpf-input>
            </ca-field>
          </ca-col>
          <ca-col size="6">
            <ca-field label="NIS">
              <pis-input
                v-model="selfEmployed.nis"
                placeholder="PIS, PASEP ou NIT"
                required>
              </pis-input>
            </ca-field>
          </ca-col>
          <ca-col size="12">
            <ca-row>
              <ca-col size="6">
                <ca-field label="Autônomo possui matrícula vinculada ao eSocial?">
                  <e-social-registration-options
                    v-model="selfEmployed.registrationOption"
                    :registration-options="misc.registrationOptions"
                    required
                    @change-options="onChangeRegistrationOptions">
                  </e-social-registration-options>
                </ca-field>
              </ca-col>
              <ca-col size="6">
                <ca-field
                  :label="registrationLabel"
                  :icon-tooltip="registrationIconTooltipDesc">
                  <e-social-registration-input
                    v-model="selfEmployed.registration"
                    :required="registrationInputEnable"
                    :disabled="!registrationInputEnable"></e-social-registration-input>
                  <small>{{ registrationExtraLabel }}</small>
                </ca-field>
              </ca-col>
            </ca-row>
          </ca-col>
          <ca-col size="6">
            <ca-field label="Sexo">
              <ca-select
                v-model="selfEmployed.gender.name">
                <ca-option
                  v-for="status in misc.genderTypes"
                  :key="status.name"
                  :value="status.name">
                  {{ status.description }}
                </ca-option>
              </ca-select>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="Raça/Cor">
              <ca-select
                v-model="selfEmployed.raceColor.name">
                <ca-option
                  v-for="option in misc.raceColorTypes"
                  :key="option.value"
                  :value="option.name">
                  {{ option.description }}
                </ca-option>
              </ca-select>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="Grau de Instrução">
              <ca-select
                v-model="selfEmployed.educationLevel.name">
                <ca-option
                  v-for="option in misc.educationLevelTypes"
                  :key="option.value"
                  :value="option.name">
                  {{ option.description }}
                </ca-option>
              </ca-select>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="Data de Nascimento">
              <ca-date-input
                v-model="selfEmployed.dateOfBirth"
                required>
              </ca-date-input>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="Categoria">
              <ca-select
                v-model="selfEmployed.category.name">
                <ca-option
                  v-for="category in misc.selfEmployedCategories"
                  :key="category.name"
                  :value="category.name">
                  {{ category.description }}
                </ca-option>
              </ca-select>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="Data do Início das Atividades">
              <ca-date-input
                v-model="selfEmployed.admissionDate"
                required>
              </ca-date-input>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="Natureza da atividade">
              <ca-select
                v-model="selfEmployed.natureActivityWork.name">
                <ca-option
                  v-for="activityNature in misc.natureActivityWorkTypes"
                  :key="activityNature.name"
                  :value="activityNature.name">
                  {{ activityNature.description }}
                </ca-option>
              </ca-select>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="Alíquota ISS">
              <percent-input
                v-model="selfEmployed.issAliquot"
                :integer-places="1"
                :decimal-places="4"
                required>
              </percent-input>
            </ca-field>
          </ca-col>

          <ca-col size="3">
            <ca-field label="Calcular IRPF">
              <ca-checkbox
                v-model="selfEmployed.calculateIrpf"
                label="Sim">
              </ca-checkbox>
            </ca-field>
          </ca-col>

          <ca-col size="3">
            <ca-field label="Calcular INSS">
              <ca-checkbox
                v-model="selfEmployed.calculateInss"
                label="Sim">
              </ca-checkbox>
            </ca-field>
          </ca-col>

          <ca-col v-if="shouldShowFieldsInss" size="3">
            <ca-field
              label="Alíquota INSS">
              <percent-input
                v-model="selfEmployed.inssAliquot"
                :integer-places="2"
                :required="shouldShowFieldsInss">
              </percent-input>
            </ca-field>
          </ca-col>

          <ca-col v-if="shouldShowFieldsInss" size="3">
            <ca-field
              label="Quantidade de Dependentes">
              <ca-input
                v-model="selfEmployed.dependentNumber"
                v-mask="'###'"
                :required="shouldShowFieldsInss"
                min="0"
                type="number">
              </ca-input>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="Vínculo com outra empresa">
              <ca-select v-model="selfEmployed.otherCompanyInfo.name">
                <ca-option
                  v-for="record in misc.otherCompanyInfoTypes"
                  :key="record.name"
                  :value="record.name">
                  {{ record.description }}
                </ca-option>
              </ca-select>
            </ca-field>
          </ca-col>
          <ca-col
            v-if="isDiscountedContribution"
            size="6">
            <ca-field
              label="Tipo Inscrição da Outra Empresa">
              <ca-select v-model="selfEmployed.otherCompanyRegistrationType.name">
                <ca-option
                  v-for="inscriptionType in misc.companyInscriptionType"
                  :key="inscriptionType.name"
                  :value="inscriptionType.name">
                  {{ inscriptionType.description }}
                </ca-option>
              </ca-select>
            </ca-field>
          </ca-col>

          <ca-col
            v-if="isDiscountedContribution"
            size="6">
            <ca-field label="Inscrição da Outra Empresa">
              <ca-input
                v-model="selfEmployed.otherCompanyRegistration"
                maxlength="20">
              </ca-input>
            </ca-field>
          </ca-col>

          <ca-col
            v-if="isDiscountedContribution"
            size="6">
            <ca-field label="Base de cálculo do INSS na Outra Empresa">
              <ca-input-currency
                v-model="selfEmployed.inssCalculationBasisOtherCompany"
                maxlength="7">
              </ca-input-currency>
            </ca-field>
          </ca-col>

          <ca-col
            v-if="isDiscountedContribution"
            size="6">
            <ca-field label="Valor descontado de INSS na Outra Empresa">
              <ca-input-currency
                v-model="selfEmployed.inssDiscountedAmountOtherCompany"
                maxlength="7">
              </ca-input-currency>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="CEP">
              <ca-cep-data-search
                v-model="selfEmployed.zipCode"
                required
                :on-search="onCepSearch"
                :on-search-success="onCepSearchSuccess"
                :on-search-error="onCepSearchError">
              </ca-cep-data-search>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="Tipo Logradouro">
              <ca-select
                v-model="selfEmployed.streetType.name">
                <ca-option
                  v-for="street in misc.streetTypes"
                  :key="street.name"
                  :value="street.name">
                  {{ street.description }}
                </ca-option>
              </ca-select>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="Logradouro">
              <ca-input
                v-model="selfEmployed.street"
                maxlength="80"
                required>
              </ca-input>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="Número">
              <ca-input
                v-model="selfEmployed.streetNumber"
                placeholder="Se não houver número, preencha com 'S/N'"
                type:="number"
                required>
              </ca-input>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="Complemento">
              <ca-input
                v-model="selfEmployed.complement"
                maxlength="30">
              </ca-input>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="Bairro">
              <ca-input
                v-model="selfEmployed.neighborhood"
                maxlength="60">
              </ca-input>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="Cidade">
              <ca-input
                v-model="selfEmployed.city"
                maxlength="50"
                required>
              </ca-input>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="Código do Município">
              <city-code-input v-model="selfEmployed.cityCode"></city-code-input>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="Estado">
              <ca-search-select
                v-model="selfEmployed.state.name"
                :selected-option="selectedUf"
                placeholder="Selecione"
                required>
                <ca-option
                  v-for="state in misc.uf"
                  :key="state.name"
                  :value="state.name"
                  placeholder="Selecione">
                  {{ state.description }}
                </ca-option>
              </ca-search-select>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="Telefone">
              <ca-input
                v-model="selfEmployed.phone"
                v-mask="['(##) ####-####', '(##) #####-####']"
                required>
              </ca-input>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field label="Email">
              <ca-email-input
                v-model="selfEmployed.email"
                maxlength="60">
              </ca-email-input>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field
              label="Situação no eSocial"
              icon-tooltip="'Novo' para trabalhadores que foram admitidos antes
                da obrigatoriedade do eSocial, 'Admissão' para trabalhadores admitidos
                a partir do início da obrigatoriedade, 'Alterado' para habilitar o envio
                alterações no contrato e 'Sincronizado' se o trabalhador já foi enviado
                por outro sistema.">
              <ca-select
                v-model="selfEmployed.esocialStatus.name">
                <ca-option
                  v-for="status in misc.esocialStatus.default"
                  :key="status.name"
                  :value="status.value">
                  {{ status.description }}
                </ca-option>
              </ca-select>
            </ca-field>
          </ca-col>
        </ca-row>

        <ca-footer>
          <ca-row vertical-offset="2">
            <ca-col size="2">
              <ca-button full-width @click="goBack">
                Voltar
              </ca-button>
            </ca-col>
            <ca-col offset="8" size="2">
              <ca-submit-button>
                Salvar
              </ca-submit-button>
            </ca-col>
          </ca-row>
        </ca-footer>
      </ca-form>
    </ca-page>
  </div>
</template>

<script>
import moment from 'moment';
import { isUndefined } from 'lodash';
import { initFormObject, getBoxInformationErrorMessage, getErrorMessageFromResponse } from 'packages/common/utils';
import { registrationActionMixin, registrationLabelMixin } from 'packages/components/e-social-registration/eSocialRegistrationMixin';
import { enableRegistrationInput } from 'packages/components/e-social-registration/eSocialRegistrationService';
import { getCepInformation } from 'accountant/employer/establishment/establishmentService';
import {
  getSelfEmployed,
  createNewSelfEmployed,
  editSelfEmployed,
} from './selfEmployedService';

export default {
  name: 'SelfEmployedFormPage',
  mixins: [registrationLabelMixin, registrationActionMixin],
  data() {
    return {
      formKey: 0,
      selfEmployedId: this.$route.params.id,
      selfEmployed: {
        ...initFormObject([
          { natureActivityWork: 'URBAN' },
          { gender: 'MALE' },
          { raceColor: 'WHITE' },
          { educationLevel: 'HIGH_COMPLETE' },
          { category: 'O701' },
          { otherCompanyInfo: 'NOT_LINKED' },
          { esocialStatus: 'N' },
          { state: 'SC' },
          { streetType: 'STREET' },
        ]),
        admissionDate: moment().format('YYYY-MM-DD'),
        calculateInss: false,
        calculateIrpf: false,
        registration: '',
        issAliquot: 0,
        inssAliquot: 0,
        jobDescription: {},
        registrationOption: {
          value: 'FIRST_REGISTRATION',
          name: 'FIRST_REGISTRATION',
        },
        otherCompanyRegistrationType: {},
      },
      showLoading: true,
      errorMessage: null,
      selectedUf: null,
      shouldShowInssFields: false,
    };
  },
  computed: {
    hasSelfEmployedId() {
      return !isUndefined(this.selfEmployedId);
    },
    misc() {
      return this.$store.state.misc;
    },
    isDiscountedContribution() {
      return this.selfEmployed.otherCompanyInfo.name === 'TRIBUTE_DEDUCED_PARTIALLY_THERE'
        || this.selfEmployed.otherCompanyInfo.name === 'TRIBUTE_DEDUCED_TOTALLY_THERE';
    },
    shouldShowFieldsInss() {
      return this.selfEmployed.calculateInss;
    },
  },
  watch: {
    shouldShowFieldsInss() {
      if (!this.selfEmployed.calculateInss) {
        this.$set(this.selfEmployed, 'inssAliquot', 0);
        this.$set(this.selfEmployed, 'dependentNumber', 0);
      }
      this.refreshForm();
    },
  },
  created() {
    this.initData();
  },
  methods: {
    initData() {
      this.$store.dispatch('misc/getEsocialStatus');
      this.$store.dispatch('misc/getRaceColorTypes');
      this.$store.dispatch('misc/getSelfEmployedCategories');
      this.$store.dispatch('misc/getEducationLevelTypes');
      this.$store.dispatch('misc/getStreetTypes');
      this.$store.dispatch('misc/getUf');
      this.$store.dispatch('misc/getGenderTypes');
      this.$store.dispatch('misc/getOtherCompanyInfoTypes');
      this.$store.dispatch('misc/getCompanyInscriptionType', 'EMPRESAS');
      this.$store.dispatch('misc/getNatureActivityWorkTypes');
      this.$store.dispatch('misc/getRegistrationOptions');

      if (this.hasSelfEmployedId) {
        this.setSelfEmployed();
      } else {
        this.showLoading = false;
      }
    },
    async setSelfEmployed() {
      try {
        this.selfEmployed = await getSelfEmployed(this.selfEmployedId);
      } catch (error) {
        this.$toaster.error(getErrorMessageFromResponse(error));
      } finally {
        this.showLoading = false;
      }
    },
    onCepSearch() {
      return getCepInformation(this.selfEmployed.zipCode);
    },
    onCepSearchSuccess(response) {
      const {
        neighborhood, city, streetAddress, state, cityCode,
      } = response;

      this.$set(this.selfEmployed, 'street', streetAddress);
      this.$set(this.selfEmployed, 'neighborhood', neighborhood);
      this.$set(this.selfEmployed, 'city', city);
      this.$set(this.selfEmployed, 'cityCode', cityCode);
      this.selectedUf = { label: state, value: state };
    },
    onCepSearchError() {
      this.$toaster.error('Desculpe, não foi possível localizar o CEP neste momento');
    },
    onSubmit() {
      this.validEmail();
      return this.hasSelfEmployedId
        ? editSelfEmployed(this.selfEmployedId, this.selfEmployed)
        : createNewSelfEmployed(this.selfEmployed);
    },
    validEmail() {
      if (this.selfEmployed.email === '') {
        this.$set(this.selfEmployed, 'email', null);
      }
    },
    onSubmitSuccess() {
      this.goBack();
      this.$toaster.success('Autônomo salvo com sucesso.');
    },
    onSubmitError(error) {
      this.errorMessage = getBoxInformationErrorMessage(error);
    },
    setRegistrationOption(value) {
      this.selfEmployed.registrationOption = value ?? {
        name: '',
        value: '',
      };

      if (!enableRegistrationInput(this.getRegistrationOption())) {
        this.selfEmployed.registration = null;
      }
    },
    getRegistrationOption() {
      return this.selfEmployed.registrationOption ?? {
        name: '',
        value: '',
      };
    },
    refreshForm() {
      this.formKey++;
    },
    goBack() {
      this.$router.back();
    },
  },
};
</script>
